import { useState, useEffect } from 'react';
import { Card, Text, Button, Input, Spinner, Badge } from '@fluentui/react-components';
import { TenantService } from '@/services/tenantService';
import { Tenant } from '@/types';
import { useAuth } from '@/auth/AuthContext';
import './Tenants.css';

interface TrialInfo {
  isTrial: boolean;
  subscriptionTier: string;
  trialAccepted: boolean;
  trialAcceptedAt: string | null;
  trialEndsAt: string | null;
  trialMaxUsers: number;
  daysRemaining: number | null;
}

interface Promotion {
  id: string;
  name: string;
  description: string | null;
  applies_to: string;
  discount_type: string | null;
  discount_value: number | null;
  band_discounts: Array<{ min: number; max: number | null; percentOff: number }> | null;
  starts_at: string | null;
  ends_at: string | null;
}

export default function TenantsPage() {
  const { token } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newTenant, setNewTenant] = useState({ name: '', domain: '', subscriptionTier: 'trial', orgIndustry: '', orgCountry: '', orgSizeBracket: '' });

  // Trial management state
  const [trialInfo, setTrialInfo] = useState<TrialInfo | null>(null);
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialEndsAt, setTrialEndsAt] = useState('');
  const [trialMaxUsers, setTrialMaxUsers] = useState('5');
  const [trialSaving, setTrialSaving] = useState(false);

  // Promotions state
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [promotionsLoading, setPromotionsLoading] = useState(false);
  const [newPromoName, setNewPromoName] = useState('');
  const [newPromoScope, setNewPromoScope] = useState<'tenant' | 'all'>('tenant');
  const [newPromoDiscountType, setNewPromoDiscountType] = useState('');
  const [newPromoDiscountValue, setNewPromoDiscountValue] = useState('');
  const [newPromoStartsAt, setNewPromoStartsAt] = useState('');
  const [newPromoEndsAt, setNewPromoEndsAt] = useState('');
  const [promoSaving, setPromoSaving] = useState(false);
  const [promoError, setPromoError] = useState('');

  const loadTenants = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const data = await TenantService.getAllTenants(token);
      setTenants(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load tenants:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTenants(); }, [token]);

  // Load trial info and promotions when a tenant is selected
  useEffect(() => {
    if (!token || !selectedTenant) {
      setTrialInfo(null);
      setPromotions([]);
      return;
    }

    setTrialLoading(true);
    setPromotionsLoading(true);

    TenantService.getTenantTrial(token, selectedTenant.id)
      .then((info) => {
        setTrialInfo(info);
        if (info.trialEndsAt) {
          const d = new Date(info.trialEndsAt);
          setTrialEndsAt(d.toISOString().slice(0, 16));
        } else {
          const d = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
          setTrialEndsAt(d.toISOString().slice(0, 16));
        }
        setTrialMaxUsers(String(info.trialMaxUsers ?? 5));
      })
      .catch(() => setTrialInfo(null))
      .finally(() => setTrialLoading(false));

    TenantService.getTenantPromotions(token, selectedTenant.id)
      .then((data) => setPromotions(data))
      .catch(() => setPromotions([]))
      .finally(() => setPromotionsLoading(false));
  }, [token, selectedTenant?.id]);

  const handleTrialUpdate = async (updates: {
    trial_ends_at?: string | null;
    trial_max_users?: number;
    subscription_tier?: string;
  }) => {
    if (!token || !selectedTenant) return;
    setTrialSaving(true);
    try {
      const result = await TenantService.updateTenantTrial(token, selectedTenant.id, updates);
      setTrialInfo(result);
      if (updates.subscription_tier) {
        setSelectedTenant({ ...selectedTenant, subscriptionTier: updates.subscription_tier });
        await loadTenants();
      }
    } catch (err) {
      console.error('Failed to update trial:', err);
    } finally {
      setTrialSaving(false);
    }
  };

  const handleCreatePromotion = async () => {
    if (!token || !selectedTenant) return;
    if (!newPromoName.trim()) { setPromoError('Offer name is required'); return; }

    const hasInvoice = newPromoDiscountType !== '' && Number(newPromoDiscountValue) > 0;
    if (!hasInvoice) { setPromoError('Add a discount type and value'); return; }

    setPromoSaving(true);
    setPromoError('');
    try {
      await TenantService.createPromotion(token, {
        name: newPromoName.trim(),
        discount_type: newPromoDiscountType || undefined,
        discount_value: Number(newPromoDiscountValue) || undefined,
        starts_at: newPromoStartsAt ? new Date(newPromoStartsAt).toISOString() : undefined,
        ends_at: newPromoEndsAt ? new Date(newPromoEndsAt).toISOString() : undefined,
        applies_to: newPromoScope,
        target_id: newPromoScope === 'tenant' ? selectedTenant.id : undefined,
      });
      // Refresh promotions
      const updated = await TenantService.getTenantPromotions(token, selectedTenant.id);
      setPromotions(updated);
      setNewPromoName('');
      setNewPromoDiscountType('');
      setNewPromoDiscountValue('');
      setNewPromoStartsAt('');
      setNewPromoEndsAt('');
    } catch (err: any) {
      setPromoError(err?.response?.data?.error || 'Failed to create offer');
    } finally {
      setPromoSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!token || !newTenant.name.trim()) { setCreateError('Tenant name is required'); return; }
    setCreating(true);
    setCreateError('');
    try {
      await TenantService.createTenant(token, {
        name: newTenant.name.trim(),
        domain: newTenant.domain.trim() || undefined,
        subscriptionTier: newTenant.subscriptionTier,
        orgIndustry: newTenant.orgIndustry || undefined,
        orgCountry: newTenant.orgCountry || undefined,
        orgSizeBracket: newTenant.orgSizeBracket || undefined,
      });
      setShowCreateForm(false);
      setNewTenant({ name: '', domain: '', subscriptionTier: 'trial', orgIndustry: '', orgCountry: '', orgSizeBracket: '' });
      await loadTenants();
    } catch (err: any) {
      setCreateError(err?.response?.data?.error || err.message || 'Failed to create tenant');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (tenantId: string) => {
    if (!token || !confirm('Are you sure you want to delete this tenant? This cannot be undone.')) return;
    try {
      await TenantService.deleteTenant(token, tenantId);
      setSelectedTenant(null);
      await loadTenants();
    } catch (err) {
      console.error('Failed to delete tenant:', err);
    }
  };

  const handleStatusChange = async (tenantId: string, status: string) => {
    if (!token) return;
    try {
      await TenantService.updateTenantStatus(token, tenantId, status);
      await loadTenants();
      if (selectedTenant?.id === tenantId) {
        setSelectedTenant({ ...selectedTenant, status: status as Tenant['status'] });
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const filteredTenants = tenants.filter(
    (tenant) =>
      tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tenant.domain.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="tenants-loading">
        <Spinner size="extra-large" />
        <Text>Loading tenants...</Text>
      </div>
    );
  }

  return (
    <div className="tenants-container">
      <div className="page-header">
        <div>
          <h1>Manage Tenants</h1>
          <Text size={300}>View and manage all Sherpai tenants</Text>
        </div>
        <Button appearance="primary" onClick={() => setShowCreateForm(true)}>
          + New Tenant
        </Button>
      </div>

      {showCreateForm && (
        <Card style={{ marginBottom: '16px', padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px' }}>Create New Tenant</h3>
          {createError && <div className="error-banner" style={{ marginBottom: '12px' }}>{createError}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div>
              <Text size={200} weight="bold" style={{ display: 'block', marginBottom: '4px' }}>Organisation Name *</Text>
              <Input value={newTenant.name} onChange={(_, d) => setNewTenant(p => ({ ...p, name: d.value }))} placeholder="Contoso Ltd" style={{ width: '100%' }} />
            </div>
            <div>
              <Text size={200} weight="bold" style={{ display: 'block', marginBottom: '4px' }}>Domain</Text>
              <Input value={newTenant.domain} onChange={(_, d) => setNewTenant(p => ({ ...p, domain: d.value }))} placeholder="contoso.com" style={{ width: '100%' }} />
            </div>
            <div>
              <Text size={200} weight="bold" style={{ display: 'block', marginBottom: '4px' }}>Industry</Text>
              <Input value={newTenant.orgIndustry} onChange={(_, d) => setNewTenant(p => ({ ...p, orgIndustry: d.value }))} placeholder="Technology" style={{ width: '100%' }} />
            </div>
            <div>
              <Text size={200} weight="bold" style={{ display: 'block', marginBottom: '4px' }}>Country</Text>
              <Input value={newTenant.orgCountry} onChange={(_, d) => setNewTenant(p => ({ ...p, orgCountry: d.value }))} placeholder="Australia" style={{ width: '100%' }} />
            </div>
            <div>
              <Text size={200} weight="bold" style={{ display: 'block', marginBottom: '4px' }}>Organisation Size</Text>
              <Input value={newTenant.orgSizeBracket} onChange={(_, d) => setNewTenant(p => ({ ...p, orgSizeBracket: d.value }))} placeholder="51-200" style={{ width: '100%' }} />
            </div>
            <div>
              <Text size={200} weight="bold" style={{ display: 'block', marginBottom: '4px' }}>Tier</Text>
              <select value={newTenant.subscriptionTier} onChange={(e) => setNewTenant(p => ({ ...p, subscriptionTier: e.target.value }))} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}>
                <option value="trial">Trial</option>
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button appearance="primary" onClick={handleCreate} disabled={creating}>
              {creating ? <Spinner size="tiny" /> : 'Create Tenant'}
            </Button>
            <Button appearance="secondary" onClick={() => { setShowCreateForm(false); setCreateError(''); }}>Cancel</Button>
          </div>
        </Card>
      )}

      <div className="tenants-content">
        <div className="tenants-list">
          <Card className="search-card">
            <Input
              placeholder="Search by name or domain..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(e.target.value)
              }
              style={{ width: '100%' }}
            />
          </Card>

          {filteredTenants.length === 0 && !loading && (
            <Card style={{ padding: '32px', textAlign: 'center' }}>
              <Text size={300}>No tenants found. Create one or register via the Customer Portal.</Text>
            </Card>
          )}

          <div className="tenants-grid">
            {filteredTenants.map((tenant) => (
              <Card
                key={tenant.id}
                className={`tenant-card ${selectedTenant?.id === tenant.id ? 'active' : ''}`}
                onClick={() => setSelectedTenant(tenant)}
              >
                <div className="tenant-header">
                  <h3>{tenant.name}</h3>
                  <Badge
                    appearance="outline"
                    color={
                      tenant.status === 'active'
                        ? 'success'
                        : tenant.status === 'suspended'
                          ? 'danger'
                          : 'warning'
                    }
                  >
                    {tenant.status}
                  </Badge>
                </div>
                <Text size={200} className="tenant-email">
                  {tenant.domain}
                </Text>
                <div className="tenant-stats">
                  <div className="stat">
                    <Text size={300} weight="bold">
                      {(tenant.subscriptionTier || '').toUpperCase()}
                    </Text>
                    <Text size={200}>Tier</Text>
                  </div>
                  <div className="stat">
                    <Text size={300} weight="bold">
                      {tenant.orgCountry || '—'}
                    </Text>
                    <Text size={200}>Country</Text>
                  </div>
                  <div className="stat">
                    <Text size={300} weight="bold">
                      {tenant.licensedUsers || 0}
                    </Text>
                    <Text size={200}>Licences</Text>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {selectedTenant && (
          <div className="tenant-details">
            <Card>
              <h2>{selectedTenant.name}</h2>
              <div className="details-grid">
                <div className="detail-item">
                  <Text size={200} weight="bold">Domain</Text>
                  <Text>{selectedTenant.domain}</Text>
                </div>
                <div className="detail-item">
                  <Text size={200} weight="bold">Status</Text>
                  <Badge
                    appearance="outline"
                    color={
                      selectedTenant.status === 'active'
                        ? 'success'
                        : selectedTenant.status === 'suspended'
                          ? 'danger'
                          : 'warning'
                    }
                  >
                    {selectedTenant.status}
                  </Badge>
                </div>
                <div className="detail-item">
                  <Text size={200} weight="bold">Subscription Tier</Text>
                  <Text>{(selectedTenant.subscriptionTier || '').toUpperCase()}</Text>
                </div>
                <div className="detail-item">
                  <Text size={200} weight="bold">Industry</Text>
                  <Text>{selectedTenant.orgIndustry || '—'}</Text>
                </div>
                <div className="detail-item">
                  <Text size={200} weight="bold">Country</Text>
                  <Text>{selectedTenant.orgCountry || '—'}</Text>
                </div>
                <div className="detail-item">
                  <Text size={200} weight="bold">Organisation Size</Text>
                  <Text>{selectedTenant.orgSizeBracket || '—'}</Text>
                </div>
                <div className="detail-item">
                  <Text size={200} weight="bold">Licensed Users</Text>
                  <Text>{selectedTenant.licensedUsers || 0}</Text>
                </div>
                <div className="detail-item">
                  <Text size={200} weight="bold">Created</Text>
                  <Text>{new Date(selectedTenant.createdAt).toLocaleDateString()}</Text>
                </div>
              </div>

              <div className="detail-actions">
                {selectedTenant.status === 'active' && (
                  <Button appearance="secondary" onClick={() => handleStatusChange(selectedTenant.id, 'suspended')}>
                    Suspend Tenant
                  </Button>
                )}
                {selectedTenant.status === 'suspended' && (
                  <Button appearance="secondary" onClick={() => handleStatusChange(selectedTenant.id, 'active')}>
                    Reactivate Tenant
                  </Button>
                )}
                <Button
                  appearance="secondary"
                  className="btn-danger"
                  onClick={() => handleDelete(selectedTenant.id)}
                >
                  Delete Tenant
                </Button>
              </div>
            </Card>

            {/* Trial Management */}
            <Card>
              <h3>Trial Management</h3>
              {trialLoading ? (
                <Spinner size="small" />
              ) : trialInfo ? (
                <>
                  <div className="details-grid">
                    <div className="detail-item">
                      <Text size={200} weight="bold">Subscription Tier</Text>
                      <Badge
                        appearance="filled"
                        color={trialInfo.isTrial ? 'warning' : 'success'}
                      >
                        {trialInfo.subscriptionTier}
                      </Badge>
                    </div>
                    <div className="detail-item">
                      <Text size={200} weight="bold">Days Remaining</Text>
                      <Text>{trialInfo.isTrial && trialInfo.daysRemaining !== null ? `${trialInfo.daysRemaining} days` : '—'}</Text>
                    </div>
                    <div className="detail-item">
                      <Text size={200} weight="bold">Max Trial Users</Text>
                      <Text>{trialInfo.trialMaxUsers}</Text>
                    </div>
                    <div className="detail-item">
                      <Text size={200} weight="bold">Trial Accepted</Text>
                      <Text>{trialInfo.trialAccepted ? 'Yes' : 'No'}</Text>
                    </div>
                  </div>

                  {trialInfo.isTrial && (
                    <>
                      <div className="form-grid">
                        <div className="form-field">
                          <label>Trial End Date</label>
                          <Input
                            type="datetime-local"
                            value={trialEndsAt}
                            onChange={(_, d) => setTrialEndsAt(d.value)}
                          />
                        </div>
                        <div className="form-field">
                          <label>Max Users</label>
                          <Input
                            type="number"
                            min={1}
                            max={100}
                            value={trialMaxUsers}
                            onChange={(_, d) => setTrialMaxUsers(d.value)}
                          />
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: 'var(--space-sm)' }}>
                        <Button
                          appearance="primary"
                          disabled={trialSaving}
                          onClick={() => handleTrialUpdate({
                            trial_ends_at: trialEndsAt ? new Date(trialEndsAt).toISOString() : null,
                            trial_max_users: parseInt(trialMaxUsers, 10) || 5,
                          })}
                        >
                          {trialSaving ? 'Saving...' : 'Update Trial'}
                        </Button>
                        <Button
                          appearance="secondary"
                          disabled={trialSaving}
                          onClick={() => {
                            const newEnd = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
                            setTrialEndsAt(newEnd.slice(0, 16));
                            setTrialMaxUsers('5');
                            handleTrialUpdate({ trial_ends_at: newEnd, trial_max_users: 5 });
                          }}
                        >
                          Reset to 7 Days
                        </Button>
                        <Button
                          appearance="secondary"
                          disabled={trialSaving}
                          onClick={() => handleTrialUpdate({ subscription_tier: 'basic', trial_ends_at: null })}
                        >
                          End Trial → Basic
                        </Button>
                      </div>
                    </>
                  )}

                  {!trialInfo.isTrial && (
                    <Button
                      appearance="secondary"
                      disabled={trialSaving}
                      onClick={() => handleTrialUpdate({
                        subscription_tier: 'trial',
                        trial_ends_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                        trial_max_users: 5,
                      })}
                    >
                      Start New 7-Day Trial
                    </Button>
                  )}
                </>
              ) : (
                <Text size={200}>Unable to load trial information.</Text>
              )}
            </Card>

            {/* Active Promotions / Discounts */}
            <Card>
              <h3>Promotions & Discounts</h3>
              {promotionsLoading ? (
                <Spinner size="small" />
              ) : promotions.length > 0 ? (
                <div className="promo-list">
                  {promotions.map((promo) => (
                    <div key={promo.id} className="promo-card">
                      <div className="promo-item">
                        <div>
                          <Text weight="bold">{promo.name}</Text>
                          {promo.description && <Text size={200} style={{ display: 'block' }}>{promo.description}</Text>}
                          <Text size={200} style={{ display: 'block', color: 'var(--text-muted)' }}>
                            {promo.applies_to === 'all' ? 'Global' : promo.applies_to === 'group' ? 'Customer group' : 'Tenant-specific'}
                            {promo.starts_at && ` • From ${new Date(promo.starts_at).toLocaleDateString()}`}
                            {promo.ends_at && ` to ${new Date(promo.ends_at).toLocaleDateString()}`}
                          </Text>
                        </div>
                        <div className="promo-badges">
                          {promo.band_discounts?.map((band) => (
                            <Badge key={`${band.min}-${band.max ?? 'plus'}`} appearance="tint" color="informative">
                              {band.max === null ? `${band.min}+` : `${band.min}-${band.max}`}: {band.percentOff}% off
                            </Badge>
                          ))}
                          {promo.discount_type && promo.discount_value !== null && (
                            <Badge appearance="filled" color="success">
                              {promo.discount_type === 'percentage'
                                ? `${promo.discount_value}% invoice discount`
                                : `$${promo.discount_value} credit`}
                            </Badge>
                          )}
                          <Button
                            appearance="subtle"
                            size="small"
                            className="btn-danger"
                            onClick={async () => {
                              if (!token) return;
                              try {
                                await TenantService.deactivatePromotion(token, promo.id);
                                setPromotions((prev) => prev.filter((p) => p.id !== promo.id));
                              } catch (err) {
                                console.error('Failed to remove promotion:', err);
                              }
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Text size={200} style={{ display: 'block', marginBottom: 'var(--space-md)' }}>No active promotions or discounts applied to this tenant.</Text>
              )}

              {/* Create Promotion Form */}
              <div className="section-divider">
                <Text size={200} weight="bold" style={{ display: 'block', marginBottom: 'var(--space-sm)' }}>Create New Offer</Text>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Offer Name *</label>
                    <Input
                      value={newPromoName}
                      onChange={(_, d) => setNewPromoName(d.value)}
                      placeholder="e.g. Launch discount"
                    />
                  </div>
                  <div className="form-field">
                    <label>Scope</label>
                    <select
                      value={newPromoScope}
                      onChange={(e) => setNewPromoScope(e.target.value as 'tenant' | 'all')}
                    >
                      <option value="tenant">This tenant only</option>
                      <option value="all">All tenants</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Discount Type</label>
                    <select
                      value={newPromoDiscountType}
                      onChange={(e) => setNewPromoDiscountType(e.target.value)}
                    >
                      <option value="">No invoice discount</option>
                      <option value="percentage">Percentage off invoice</option>
                      <option value="fixed_amount">Fixed amount off invoice</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Discount Value</label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={newPromoDiscountValue}
                      onChange={(_, d) => setNewPromoDiscountValue(d.value)}
                      placeholder="e.g. 10"
                    />
                  </div>
                  <div className="form-field">
                    <label>Starts</label>
                    <Input
                      type="datetime-local"
                      value={newPromoStartsAt}
                      onChange={(_, d) => setNewPromoStartsAt(d.value)}
                    />
                  </div>
                  <div className="form-field">
                    <label>Ends</label>
                    <Input
                      type="datetime-local"
                      value={newPromoEndsAt}
                      onChange={(_, d) => setNewPromoEndsAt(d.value)}
                    />
                  </div>
                </div>
                {promoError && <Text size={200} style={{ color: 'var(--danger)', display: 'block', marginBottom: 'var(--space-sm)' }}>{promoError}</Text>}
                <Button
                  appearance="primary"
                  disabled={promoSaving}
                  onClick={handleCreatePromotion}
                >
                  {promoSaving ? 'Creating...' : 'Create Offer'}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
